"use client";

import { Box, VStack, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useSelectionState, useUserChoices } from "@/state/globalStore";
import GenericConfirmationDialog from "@/shared/ui/GenericConfirmationDialog";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericSegment from "@/shared/ui/controls/GenericSegment";
import GenericToggle from "@/shared/ui/controls/GenericToggle";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import { MindstringPrototype, Situation } from "@/generated/fastapi";
import { SuggestedThoughtConfirmationDialog } from "@/features/thoughts";
import {
  filterSituationsByPrototypes,
  useSituationItems,
  useSituationsQuery,
} from "@/features/situations";
import {
  type DeleteSelectionConfirmation,
  useStudioConfirmationContext,
} from "../contexts/StudioConfirmationProvider";
import {
  type RelatedThoughtsDeleteAction,
  type RelatedThoughtsDeleteChoice,
  useStudioDeletionStateContext,
} from "../contexts/StudioDeletionStateProvider";
import { useStudioReassignStateContext } from "../contexts/StudioReassignStateProvider";

type DeleteSelectionDialogProps = {
  selectedThoughtsCount: number;
  selectedSituationsCount: number;
  relatedThoughtsDeleteChoice: RelatedThoughtsDeleteChoice;
  setRelatedThoughtsDeleteChoice: (
    value: RelatedThoughtsDeleteChoice,
  ) => void;
  anchorThoughtsToSituation: Situation[];
  setAnchorThoughtsToSituation: (value: Situation[]) => void;
  deletedSituationIds: string[];
  relatedThoughtPrototypes: MindstringPrototype[];
  situations: Situation[];
  onClose: () => void;
  onConfirm: (value: DeleteSelectionConfirmation) => Promise<void>;
};

type RelatedThoughtDeletionActionPickerProps = {
  choice: RelatedThoughtsDeleteChoice;
  setChoice: (value: RelatedThoughtsDeleteChoice) => void;
  anchorThoughtsToSituation: Situation[];
  setAnchorThoughtsToSituation: (value: Situation[]) => void;
  deletedSituationIds: string[];
  relatedThoughtPrototypes: MindstringPrototype[];
  situations: Situation[];
};

const RELATED_THOUGHT_ACTION_OPTIONS = [
  { value: "delete", label: "Delete related thoughts" },
  { value: "remove_anchor", label: "Remove anchor" },
  { value: "anchor_to_situation", label: "Anchor elsewhere" },
] as const satisfies {
  value: RelatedThoughtsDeleteAction;
  label: string;
}[];

const RELATED_THOUGHT_ACTION_DESCRIPTIONS = {
  delete: "Related thoughts will be removed with the situation.",
  remove_anchor:
    "Confirmed thoughts stay in your profile without this situation anchor.",
  anchor_to_situation: "Confirmed thoughts move to another compatible situation.",
} as const satisfies Record<RelatedThoughtsDeleteAction, string>;

function RelatedThoughtDeletionActionPicker({
  choice,
  setChoice,
  anchorThoughtsToSituation,
  setAnchorThoughtsToSituation,
  deletedSituationIds,
  relatedThoughtPrototypes,
  situations,
}: RelatedThoughtDeletionActionPickerProps) {
  const targetSituations = useMemo(() => {
    const deletedSituationIdSet = new Set(deletedSituationIds);
    return filterSituationsByPrototypes(
      situations,
      relatedThoughtPrototypes,
    ).filter((situation) => !deletedSituationIdSet.has(situation.id));
  }, [deletedSituationIds, relatedThoughtPrototypes, situations]);
  const targetSituationItems = useSituationItems(targetSituations);

  useEffect(() => {
    if (
      anchorThoughtsToSituation.length > 0 &&
      !targetSituations.some(
        (situation) => situation.id === anchorThoughtsToSituation[0]?.id,
      )
    ) {
      setAnchorThoughtsToSituation([]);
    }
  }, [anchorThoughtsToSituation, setAnchorThoughtsToSituation, targetSituations]);

  const handleActionChange = (action: RelatedThoughtsDeleteAction) => {
    setChoice({
      action,
      targetSituationId:
        action === "anchor_to_situation"
          ? anchorThoughtsToSituation[0]?.id ?? null
          : null,
    });

    if (action !== "anchor_to_situation") {
      setAnchorThoughtsToSituation([]);
    }
  };

  const handleTargetSituationChange = (nextSituations: Situation[]) => {
    setAnchorThoughtsToSituation(nextSituations);
    setChoice({
      action: "anchor_to_situation",
      targetSituationId: nextSituations[0]?.id ?? null,
    });
  };

  return (
    <VStack align="stretch" gap="3">
      <Text fontWeight="medium">
        What should happen to confirmed thoughts anchored to deleted situations?
      </Text>
      <Box display={{ base: "block", sm: "none" }}>
        <GenericSegment<RelatedThoughtsDeleteAction>
          activeState={choice.action}
          setActiveState={handleActionChange}
          options={RELATED_THOUGHT_ACTION_OPTIONS}
          size="sm"
          orientation="vertical"
        />
      </Box>
      <Box display={{ base: "none", sm: "block" }}>
        <GenericSegment<RelatedThoughtsDeleteAction>
          activeState={choice.action}
          setActiveState={handleActionChange}
          options={RELATED_THOUGHT_ACTION_OPTIONS}
          size="sm"
        />
      </Box>
      <Text color="moveon.text.muted" fontSize="sm">
        {RELATED_THOUGHT_ACTION_DESCRIPTIONS[choice.action]}
      </Text>

      {choice.action === "anchor_to_situation" ? (
        targetSituations.length > 0 ? (
          <GenericSelector<Situation>
            previewItems={targetSituationItems}
            stateObject={anchorThoughtsToSituation}
            stateObjectSetter={handleTargetSituationChange}
            mode="select"
            sourceItems={targetSituations}
            placeholder="Anchor to situation"
          />
        ) : (
          <GenericEmptyComponent
            title="No compatible situations"
            message="Add or edit another situation that supports these perspectives before anchoring."
            icon="situation"
            variant="inline"
          />
        )
      ) : null}
    </VStack>
  );
}

function DeleteSelectionDialog({
  selectedThoughtsCount,
  selectedSituationsCount,
  relatedThoughtsDeleteChoice,
  setRelatedThoughtsDeleteChoice,
  anchorThoughtsToSituation,
  setAnchorThoughtsToSituation,
  deletedSituationIds,
  relatedThoughtPrototypes,
  situations,
  onClose,
  onConfirm,
}: DeleteSelectionDialogProps) {
  const [deleteSelectedThoughts, setDeleteSelectedThoughts] = useState(
    selectedThoughtsCount > 0,
  );
  const [deleteSelectedSituations, setDeleteSelectedSituations] = useState(
    selectedThoughtsCount === 0 && selectedSituationsCount > 0,
  );
  const effectiveDeleteSelectedThoughts = deleteSelectedSituations
    ? false
    : deleteSelectedThoughts;
  const canConfirmSelectionDeletion =
    effectiveDeleteSelectedThoughts ||
    (deleteSelectedSituations &&
      (relatedThoughtsDeleteChoice.action !== "anchor_to_situation" ||
        Boolean(relatedThoughtsDeleteChoice.targetSituationId)));
  const handleDeleteSelectedSituationsChange = (value: boolean) => {
    setDeleteSelectedSituations(value);
    setDeleteSelectedThoughts(value ? false : selectedThoughtsCount > 0);
  };

  return (
    <GenericConfirmationDialog
      isOpen={true}
      onCloseAction={onClose}
      onConfirmAction={() =>
        onConfirm({
          deleteThoughts: effectiveDeleteSelectedThoughts,
          deleteSituations: deleteSelectedSituations,
          relatedThoughtsChoice: relatedThoughtsDeleteChoice,
        })
      }
      title="Delete Selection"
      description="Choose what this action should delete. This cannot be undone."
      confirmText="Delete"
      confirmDisabled={!canConfirmSelectionDeletion}
    >
      <VStack align="stretch" gap="4">
        {selectedSituationsCount > 0 ? (
          <GenericToggle
            label={`Delete ${selectedSituationsCount} selected ${
              selectedSituationsCount === 1 ? "situation" : "situations"
            }`}
            onChange={handleDeleteSelectedSituationsChange}
            value={deleteSelectedSituations}
          />
        ) : null}
        {selectedThoughtsCount > 0 && !deleteSelectedSituations ? (
          <GenericToggle
            label={`Delete ${selectedThoughtsCount} selected ${
              selectedThoughtsCount === 1 ? "thought" : "thoughts"
            }`}
            onChange={setDeleteSelectedThoughts}
            value={deleteSelectedThoughts}
          />
        ) : null}
        {deleteSelectedSituations ? (
          <RelatedThoughtDeletionActionPicker
            choice={relatedThoughtsDeleteChoice}
            setChoice={setRelatedThoughtsDeleteChoice}
            anchorThoughtsToSituation={anchorThoughtsToSituation}
            setAnchorThoughtsToSituation={setAnchorThoughtsToSituation}
            deletedSituationIds={deletedSituationIds}
            relatedThoughtPrototypes={relatedThoughtPrototypes}
            situations={situations}
          />
        ) : null}
      </VStack>
    </GenericConfirmationDialog>
  );
}

/**
 * Centralized confirmation dialogs for Studio views
 * Handles all confirmation prompts for Studio actions
 */
export default function StudioConfirmationDialogs() {
  const situationsQuery = useSituationsQuery();
  const { countSelectedThoughts, countSelectedSituations } =
    useSelectionState();
  const { countSelectedSuggestedThoughts, selectedSituations } =
    useUserChoices();
  const {
    confirmSelectedThoughtsDeletion,
    confirmSelectedSuggestedThoughtsDeletion,
    confirmSelectedSelectionDeletion,
    confirmSelectedThoughtsResetReflections,
    confirmSelectedThoughtsConfirmation,
    confirmSituationsDeletion,
  } = useStudioConfirmationContext();
  const {
    reassignToSituation,
    setReassignToSituation,
    selectedThoughtConfirmationPrototypes,
    setSelectedThoughtConfirmationPrototypes,
    reassignAllThoughts,
    setReassignAllThoughts,
  } = useStudioReassignStateContext();
  const {
    anchorThoughtsToSituation,
    pendingSituationDeletionRequest,
    relatedThoughtsDeleteChoice,
    resetRelatedThoughtsDeleteChoice,
    setAnchorThoughtsToSituation,
    setPendingSituationDeletionRequest,
    setRelatedThoughtsDeleteChoice,
  } = useStudioDeletionStateContext();
  const selectedThoughtsCount = countSelectedThoughts();
  const selectedSituationsCount = countSelectedSituations();
  const selectedSuggestedThoughtsCount = countSelectedSuggestedThoughts();
  const selectedThoughtAnchorStrategy = reassignAllThoughts
    ? "all_requested_thoughts"
    : "unassigned_thoughts_only";
  const availableConfirmationSituations = useMemo(
    () =>
      filterSituationsByPrototypes(
        situationsQuery.data ?? [],
        selectedThoughtConfirmationPrototypes,
      ),
    [selectedThoughtConfirmationPrototypes, situationsQuery.data],
  );
  const situationItems = useSituationItems(availableConfirmationSituations);
  const canConfirmThoughtAnchoring = reassignToSituation.length > 0;
  const hasAvailableConfirmationSituations =
    availableConfirmationSituations.length > 0;
  const selectedDeletionSituationIds = useMemo(
    () =>
      selectedSituations.flatMap((selection) => [
        ...selection.situations_ids,
      ]),
    [selectedSituations],
  );
  const selectedDeletionPrototypes = useMemo(
    () =>
      Array.from(
        new Set(
          selectedSituations
            .filter((selection) => selection.situations_ids.length > 0)
            .map((selection) => selection.prototype),
        ),
      ),
    [selectedSituations],
  );
  const pendingDeletionSituationIds =
    pendingSituationDeletionRequest?.situation_ids ?? [];
  const pendingDeletionPrototypes = pendingSituationDeletionRequest?.prototype
    ? [pendingSituationDeletionRequest.prototype]
    : [];
  const canConfirmSituationDeletion =
    relatedThoughtsDeleteChoice.action !== "anchor_to_situation" ||
    Boolean(relatedThoughtsDeleteChoice.targetSituationId);
  const handleCloseSituationsDeletion = () => {
    confirmSituationsDeletion.onCloseWithCleanup(() => {
      resetRelatedThoughtsDeleteChoice();
      setPendingSituationDeletionRequest(null);
    });
  };

  return (
    <>
      {/* Dialog: Delete Selected Thoughts */}
      <GenericConfirmationDialog
        isOpen={confirmSelectedThoughtsDeletion.isOpen}
        onCloseAction={confirmSelectedThoughtsDeletion.onClose}
        onConfirmAction={confirmSelectedThoughtsDeletion.onConfirm}
        title="Delete Selected Thoughts"
        description={`Are you sure you want to delete ${selectedThoughtsCount} selected thoughts? This action cannot be undone.`}
        confirmText="Delete"
      />

      {/* Dialog: Delete Selected Suggested Thoughts */}
      <GenericConfirmationDialog
        isOpen={confirmSelectedSuggestedThoughtsDeletion.isOpen}
        onCloseAction={confirmSelectedSuggestedThoughtsDeletion.onClose}
        onConfirmAction={confirmSelectedSuggestedThoughtsDeletion.onConfirm}
        title="Delete Selected Suggestions"
        description={`Are you sure you want to delete ${selectedSuggestedThoughtsCount} selected suggested thoughts? This action cannot be undone.`}
        confirmText="Delete"
      />

      {confirmSelectedSelectionDeletion.isOpen ? (
        <DeleteSelectionDialog
          selectedThoughtsCount={selectedThoughtsCount}
          selectedSituationsCount={selectedSituationsCount}
          relatedThoughtsDeleteChoice={relatedThoughtsDeleteChoice}
          setRelatedThoughtsDeleteChoice={setRelatedThoughtsDeleteChoice}
          anchorThoughtsToSituation={anchorThoughtsToSituation}
          setAnchorThoughtsToSituation={setAnchorThoughtsToSituation}
          deletedSituationIds={selectedDeletionSituationIds}
          relatedThoughtPrototypes={selectedDeletionPrototypes}
          situations={situationsQuery.data ?? []}
          onClose={() =>
            confirmSelectedSelectionDeletion.onCloseWithCleanup(() => {
              resetRelatedThoughtsDeleteChoice();
            })
          }
          onConfirm={async (confirmation) => {
            await confirmSelectedSelectionDeletion.onConfirm(confirmation);
            resetRelatedThoughtsDeleteChoice();
          }}
        />
      ) : null}

      {/* Dialog: Reset Reflections for Selected Thoughts */}
      <GenericConfirmationDialog
        isOpen={confirmSelectedThoughtsResetReflections.isOpen}
        onCloseAction={confirmSelectedThoughtsResetReflections.onClose}
        onConfirmAction={confirmSelectedThoughtsResetReflections.onConfirm}
        title="Reset All Reflections"
        description={`Are you sure you want to reset reflections for ${selectedThoughtsCount} selected thoughts? This action cannot be undone.`}
        confirmText="Reset"
      />

      {/* Dialog: Delete Situations */}
      <GenericConfirmationDialog
        isOpen={confirmSituationsDeletion.isOpen}
        onCloseAction={handleCloseSituationsDeletion}
        onConfirmAction={async () => {
          await confirmSituationsDeletion.onConfirm(relatedThoughtsDeleteChoice);
          resetRelatedThoughtsDeleteChoice();
          setPendingSituationDeletionRequest(null);
        }}
        title="Delete situations"
        description="Choose what should happen to the situation and its anchored confirmed thoughts. This cannot be undone."
        confirmText="Delete"
        confirmDisabled={!canConfirmSituationDeletion}
      >
        <VStack align="stretch">
          <RelatedThoughtDeletionActionPicker
            choice={relatedThoughtsDeleteChoice}
            setChoice={setRelatedThoughtsDeleteChoice}
            anchorThoughtsToSituation={anchorThoughtsToSituation}
            setAnchorThoughtsToSituation={setAnchorThoughtsToSituation}
            deletedSituationIds={pendingDeletionSituationIds}
            relatedThoughtPrototypes={pendingDeletionPrototypes}
            situations={situationsQuery.data ?? []}
          />
        </VStack>
      </GenericConfirmationDialog>

      {/* Dialog: Confirm MULTIPLE Thought Anchoring to Situation */}
      <GenericConfirmationDialog
        isOpen={confirmSelectedThoughtsConfirmation.isOpen}
        onCloseAction={() =>
          confirmSelectedThoughtsConfirmation.onCloseWithCleanup(() => {
            setReassignToSituation([]);
            setSelectedThoughtConfirmationPrototypes([]);
          })
        }
        onConfirmAction={async () => {
          await confirmSelectedThoughtsConfirmation.onConfirm({
            situationId: reassignToSituation[0]?.id,
            assignStrategy: selectedThoughtAnchorStrategy,
          });
          setReassignToSituation([]);
          setSelectedThoughtConfirmationPrototypes([]);
        }}
        title="Anchor thoughts"
        description={
          "Some selected thoughts are not anchored to a situation yet."
        }
        confirmText="Confirm"
        confirmDisabled={!canConfirmThoughtAnchoring}
      >
        <VStack align="stretch">
          {hasAvailableConfirmationSituations ? (
            <>
              <Text>Choose where these thoughts should be anchored:</Text>
              <GenericSelector<Situation>
                previewItems={situationItems}
                stateObject={reassignToSituation}
                stateObjectSetter={setReassignToSituation}
                mode="select"
                sourceItems={availableConfirmationSituations}
                placeholder="Anchor to situation"
              />
            </>
          ) : (
            <GenericEmptyComponent
              title="No compatible situations"
              message="Add or edit a situation that supports every selected perspective before confirming."
              icon="situation"
              variant="inline"
            />
          )}
          <Text>Choose anchoring strategy:</Text>
          <GenericToggle
            label={
              reassignAllThoughts
                ? "Anchor all selected thoughts"
                : "Anchor only unassigned thoughts"
            }
            value={reassignAllThoughts}
            onChange={(newValue) => setReassignAllThoughts(newValue)}
          />
        </VStack>
      </GenericConfirmationDialog>
      <SuggestedThoughtConfirmationDialog />
    </>
  );
}
